import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { DropdownMenusModule } from 'src/app/_metronic/partials';
import { FormImportNewComponent } from '../../import/components/form-import-new/form-import-new.component';
import {
  AcademicHolidaySaveAction,
  AcademicHolidaySaveFailedEvent,
  FormAddAcademicHolidayComponent,
} from '../../import/components/form-add-academic-holiday/form-add-academic-holiday.component';
import { AcademicHolidayService } from 'src/app/services/api/academic-holidays/academic-holiday.service';
import { AcademicHolidayDataSource, AcademicHolidayListResponse, AcademicHolidayModel, AcademicHolidayRow } from 'src/app/services/models/academic-holidays/academic_holidays.model';
import { SharedModule } from 'src/shared/shared.module';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { getSafeErrorText } from 'src/utils/http-error-message';



type PublicHolidayAction = 'download-template' | 'import' | 'create' | 'edit' | 'delete';

@Component({
  selector: 'app-pubblic-holiday',
  imports: [
    SharedModule,
    TranslateModule,
    DropdownMenusModule,
    FormImportNewComponent,
    FormAddAcademicHolidayComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './pubblic-holiday.component.html',
  styleUrl: './pubblic-holiday.component.scss',
})
export class PubblicHolidayComponent implements OnInit, OnDestroy {
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('tableCellAction', { static: true })
  tableCellAction!: TemplateRef<any>;
  @ViewChild(FormAddAcademicHolidayComponent)
  academicHolidayForm!: FormAddAcademicHolidayComponent;
  @ViewChild(FormImportNewComponent)
  importForm!: FormImportNewComponent;
  @ViewChild('importAcademicHolidaySuccessDialog')
  importAcademicHolidaySuccessDialog!: ConfirmDialogComponent;
  @ViewChild('deleteAcademicHolidayConfirmDialog')
  deleteAcademicHolidayConfirmDialog!: ConfirmDialogComponent;
  @ViewChild('deleteAcademicHolidaySuccessDialog')
  deleteAcademicHolidaySuccessDialog!: ConfirmDialogComponent;
  @ViewChild('deleteAcademicHolidayErrorDialog')
  deleteAcademicHolidayErrorDialog!: ConfirmDialogComponent;
  @ViewChild('createAcademicHolidaySuccessDialog')
  createAcademicHolidaySuccessDialog!: ConfirmDialogComponent;
  @ViewChild('createAcademicHolidayErrorDialog')
  createAcademicHolidayErrorDialog!: ConfirmDialogComponent;
  @ViewChild('editAcademicHolidaySuccessDialog')
  editAcademicHolidaySuccessDialog!: ConfirmDialogComponent;
  @ViewChild('editAcademicHolidayErrorDialog')
  editAcademicHolidayErrorDialog!: ConfirmDialogComponent;

  form!: FormGroup;
  columns: any[] = [];
  holidays = signal<AcademicHolidayDataSource>(this.createHolidayDataSource([]));
  tableLoading = signal(false);
  lastAction = signal<PublicHolidayAction | null>(null);
  pageSizeOptions = [10, 20, 50];
  isDeleting = false;
  isDownloadingTemplate = false;
  isImporting = false;
  canImportForYear = false;
  yearOptions: string[] = [];
  deleteAcademicHolidayErrorMessage = 'ไม่สามารถลบรายการได้ กรุณาลองใหม่อีกครั้ง';
  createAcademicHolidayErrorMessage = 'ไม่สามารถเพิ่มรายการได้ กรุณาลองใหม่อีกครั้ง';
  editAcademicHolidayErrorMessage = 'ไม่สามารถแก้ไขรายการได้ กรุณาลองใหม่อีกครั้ง';

  private fb = inject(FormBuilder);
  private academicHolidayService = inject(AcademicHolidayService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private currentPage = 1;
  private currentLimit = 50;
  private currentSortParams: DatatableSortParams = {
    sort: null,
    direction: null,
  };
  private deletingHolidayId?: number;
  private filterSubscriptions: Subscription[] = [];
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'sequence',
    term: 'academic_term_name',
    year: 'year',
    name: 'name',
    holidayDate: 'holiday_date',
  };

  ngOnInit(): void {
    this.yearOptions = this.buildYearOptions();
    this.initForm();
    this.initColumns();
    this.listenFilters();
    this.loadAcademicHolidays();
  }

  ngOnDestroy(): void {
    this.filterSubscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  onDownloadTemplate(): void {
    this.lastAction.set('download-template');

    if (this.isDownloadingTemplate || !this.canImportForYear) {
      return;
    }

    this.isDownloadingTemplate = true;

    this.academicHolidayService.exportTemplate().subscribe({
      next: (blob) => this.downloadBlob(blob, 'academic_holidays_template.xlsx'),
      error: (err) => {
        console.error(err);
        this.isDownloadingTemplate = false;
      },
      complete: () => {
        this.isDownloadingTemplate = false;
      },
    });
  }

  onImport(): void {
    if (!this.canImportForYear) {
      return;
    }

    this.lastAction.set('import');
  }

  onSearch(): void {
    this.loadAcademicHolidays(1);
  }

  onImportFile(file: File): void {
    this.lastAction.set('import');

    if (this.isImporting || !this.canImportForYear) {
      return;
    }

    this.isImporting = true;

    this.academicHolidayService.importAcademicHolidays(file).subscribe({
      next: () => {
        this.importForm.clearFileSelection();
        this.loadAcademicHolidays(1);
        this.importForm.closeAfterImportSuccess(() => {
          this.importAcademicHolidaySuccessDialog.open();
        });
      },
      error: (err) => {
        this.importForm.showImportError(err.error);
        this.isImporting = false;
      },
      complete: () => {
        this.isImporting = false;
      },
    });
  }

  onAddHoliday(): void {
    this.lastAction.set('create');
    this.academicHolidayForm.openModal();
  }

  onEditHoliday(row: AcademicHolidayRow): void {
    if (!row.canEdit) {
      return;
    }

    this.lastAction.set('edit');
    this.academicHolidayForm.openModal(row.id);
  }

  onAcademicHolidaySaved(action: AcademicHolidaySaveAction): void {
    this.loadAcademicHolidays(action === 'create' ? 1 : this.currentPage);

    if (action === 'create') {
      this.createAcademicHolidaySuccessDialog.open();
      return;
    }

    this.editAcademicHolidaySuccessDialog.open();
  }

  onAcademicHolidaySaveFailed({ action, message }: AcademicHolidaySaveFailedEvent): void {
    if (action === 'create') {
      this.openCreateAcademicHolidayErrorDialog(message);
      return;
    }

    this.openEditAcademicHolidayErrorDialog(message);
  }

  onDeleteHoliday(row: AcademicHolidayRow): void {
    if (!row.canDelete) {
      return;
    }

    this.lastAction.set('delete');
    this.deletingHolidayId = row.id;
    this.deleteAcademicHolidayConfirmDialog.open();
  }

  onConfirmDeleteHoliday(): void {
    if (this.deletingHolidayId == null || this.isDeleting) {
      return;
    }

    this.isDeleting = true;

    this.academicHolidayService.deleteAcademicHoliday(this.deletingHolidayId).subscribe({
      next: () => {
        this.deletingHolidayId = undefined;
        this.loadAcademicHolidays();
        this.closeDeleteConfirmThen(() => this.deleteAcademicHolidaySuccessDialog.open());
      },
      error: (err) => {
        console.error(err);
        this.deletingHolidayId = undefined;
        const message = this.getApiErrorMessage(
          err,
          'ไม่สามารถลบรายการได้ กรุณาลองใหม่อีกครั้ง'
        );

        this.closeDeleteConfirmThen(() => this.openDeleteAcademicHolidayErrorDialog(message));
        this.isDeleting = false;
      },
      complete: () => {
        this.isDeleting = false;
      },
    });
  }

  onCancelDeleteHoliday(): void {
    this.deletingHolidayId = undefined;
  }

  loadAcademicHolidays(page = this.currentPage): void {
    this.tableLoading.set(true);
    this.currentPage = page;
    const { year, keyword } = this.form.getRawValue();

    this.academicHolidayService.getAcademicHolidaysList({
      year: this.toChristianYear(year),
      keyword,
      page: this.currentPage,
      limit: this.currentLimit,
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
    }).subscribe({
      next: (res) => {
        this.canImportForYear = res.can_import_for_year === true;
        this.holidays.set(this.createHolidayDataSource(res));
        this.tableLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.canImportForYear = false;
        this.holidays.set(this.createHolidayDataSource([]));
        this.tableLoading.set(false);
      },
    });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.holidays().total_page || page === this.currentPage) {
      return;
    }

    this.loadAcademicHolidays(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadAcademicHolidays(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadAcademicHolidays(1);
  }

  private initForm(): void {
    this.form = this.fb.group({
      year: [String(this.getDefaultBuddhistYear())],
      keyword: [''],
    });
  }

  private listenFilters(): void {
    const keywordSubscription = this.form.get('keyword')?.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadAcademicHolidays(1));

    const yearSubscription = this.form.get('year')?.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => this.loadAcademicHolidays(1));

    if (keywordSubscription != null) {
      this.filterSubscriptions.push(keywordSubscription);
    }

    if (yearSubscription != null) {
      this.filterSubscriptions.push(yearSubscription);
    }
  }

  private initColumns(): void {
    this.columns = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 64,
        minWidth: 64,
        maxWidth: 64,
      },
      {
        prop: 'term',
        name: 'เทอมการศึกษา',
        sortable: true,
        width: 140,
        minWidth: 140,
        maxWidth: 140,
      },
      {
        prop: 'year',
        name: 'ปีการศึกษา',
        sortable: false,
        width: 120,
        minWidth: 120,
        maxWidth: 120,
      },
      {
        prop: 'name',
        name: 'ชื่อวันหยุดราชการ',
        sortable: true,
      },
      {
        prop: 'holidayDate',
        name: 'วันที่หยุดราชการ',
        sortable: true,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.tableCellAction,
        width: 104,
        minWidth: 104,
        maxWidth: 104,
        cellClass: 'action-cell',
        headerClass: 'action-header',
      },
    ];
  }

  private createHolidayDataSource(response: AcademicHolidayListResponse | AcademicHolidayModel[]): AcademicHolidayDataSource {
    const items = Array.isArray(response) ? response : response.data;
    const page = Array.isArray(response) ? 1 : response.page;
    const limit = Array.isArray(response) ? this.currentLimit : response.limit;
    const total = Array.isArray(response) ? items.length : response.total;
    const totalPage = Array.isArray(response) ? Math.max(1, Math.ceil(total / limit)) : response.total_pages;
    const data = items.map((item) => ({
      id: item.id,
      sequence: item.sequence,
      term: item.academic_term?.name ?? '-',
      year: this.toBuddhistYear(item.year ?? item.academic_term?.year ?? 0),
      name: item.name,
      holidayDate: this.formatDate(item.holiday_date ?? item.start_date),
      canEdit: item.can_edit === true,
      canDelete: item.can_delete === true,
    }));

    return {
      data,
      limit,
      offset: (page - 1) * limit,
      page,
      total,
      total_page: totalPage,
    };
  }

  // private formatDate(value?: string): string {
  //   if (value == null || value === '') {
  //     return '-';
  //   }

  //   const date = new Date(value);

  //   if (Number.isNaN(date.getTime())) {
  //     return value;
  //   }

  //   const day = String(date.getDate()).padStart(2, '0');
  //   const month = String(date.getMonth() + 1).padStart(2, '0');
  //   const year = date.getFullYear();

  //   return `${day}/${month}/${year}`;
  // }

  private formatDate(value?: string): string {
  if (value == null || value === '') {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear() + 543;

  return `${day}/${month}/${year}`;
}
  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();

    window.URL.revokeObjectURL(url);
  }

  private toChristianYear(value: unknown): string | null {
    if (value == null || value === '' || value === 0 || value === '0') {
      return null;
    }

    const year = Number(value);

    if (!Number.isFinite(year) || year <= 0) {
      return null;
    }

    return String(year > 2400 ? year - 543 : year);
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private getDefaultBuddhistYear(): number {
    const sessionYear = this.authService.currentUser()?.academic_term?.year;
    const normalizedSessionYear = Number(sessionYear);

    if (sessionYear == null || !Number.isFinite(normalizedSessionYear) || normalizedSessionYear <= 0) {
      return this.toBuddhistYear(new Date().getFullYear());
    }

    return this.toBuddhistYear(normalizedSessionYear);
  }

  private buildYearOptions(): string[] {
    const baseYear = this.getDefaultBuddhistYear();

    return [baseYear + 1, baseYear, baseYear - 1].map((year) => String(year));
  }

  private openDeleteAcademicHolidayErrorDialog(message: string): void {
    this.deleteAcademicHolidayErrorMessage = message;
    this.cdr.detectChanges();
    this.deleteAcademicHolidayErrorDialog.open();
  }

  private closeDeleteConfirmThen(openNextDialog: () => void): void {
    const modalEl = document.getElementById('deleteAcademicHolidayConfirm');

    if (modalEl == null) {
      this.deleteAcademicHolidayConfirmDialog.close();
      openNextDialog();
      return;
    }

    modalEl.addEventListener('hidden.bs.modal', openNextDialog, { once: true });
    this.deleteAcademicHolidayConfirmDialog.close();
  }

  private openCreateAcademicHolidayErrorDialog(message: string): void {
    this.createAcademicHolidayErrorMessage = message;
    this.cdr.detectChanges();
    this.createAcademicHolidayErrorDialog.open();
  }

  private openEditAcademicHolidayErrorDialog(message: string): void {
    this.editAcademicHolidayErrorMessage = message;
    this.cdr.detectChanges();
    this.editAcademicHolidayErrorDialog.open();
  }

  private getApiErrorMessage(error: unknown, fallbackMessage: string): string {
    const responseError = (error as { error?: unknown } | null | undefined)?.error;
    const parsedResponseError = this.parseErrorResponse(responseError);

    if (parsedResponseError != null && typeof parsedResponseError === 'object') {
      const errorRecord = parsedResponseError as { errors?: unknown; error?: unknown; message?: unknown };

      if (Array.isArray(errorRecord.errors) && errorRecord.errors.length > 0) {
        const errors = errorRecord.errors
          .map((item) => this.getErrorText(item))
          .filter((item) => item !== '');

        if (errors.length > 0) {
          return errors.join('\n');
        }
      }

      const errorText = this.getErrorText(errorRecord.error);
      const messageText = this.getErrorText(errorRecord.message);
      const message = errorText !== '' ? errorText : messageText;

      return message !== '' ? message : fallbackMessage;
    }

    const responseErrorText = this.getErrorText(parsedResponseError);
    const messageText = this.getErrorText((error as { message?: unknown } | null | undefined)?.message);
    const message = responseErrorText !== '' ? responseErrorText : messageText;

    return message !== '' ? message : fallbackMessage;
  }

  private parseErrorResponse(value: unknown): unknown {
    if (typeof value !== 'string') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private getErrorText(value: unknown): string {
    return getSafeErrorText(value);
  }

}
